/* eslint-disable no-param-reassign, no-shadow */
import clsx from 'clsx'
import { noop } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { Progress } from 'reactstrap'
import { GenericPortlet } from '../../components/GenericPortlet'
import { LineChart } from '../../components/LineChart'
import { MultiSelectSearch } from '../../components/MultiSelectSearch'
import { Spinner } from '../../components/Spinner'
import { Table } from '../../components/Table'
import './LicenseReport.module.scss'
import { getTableOptions } from './utils'

const dateFormat = 'YYYY-MM-DD'
// Employee's header is only a short filter, so at 1024px it shrinks under Role, License type,
// and Status and names and emails crush. Hold it at least as wide as those columns so a name
// and email can wrap on words; it still grows when the table has room.
const EMPLOYEE_COLUMN_MIN_WIDTH = '240px'
const employeeColumnHeaderStyle = () => ({ minWidth: EMPLOYEE_COLUMN_MIN_WIDTH })

// local storage saved selected licenses key
const key = 'license-report-filter-licenses'

const propTypes = {
  /**
   * Indicates if data for the report is being fetched
   */
  isLoading: PropTypes.bool,
  /**
   * Indicates if data for the report failed to load
   */
  isError: PropTypes.bool,
  /**
   * Report data
   */
  licenseReportData: PropTypes.shape({
    trends: PropTypes.arrayOf(
      PropTypes.shape({
        assignedCount: PropTypes.number,
        enabledCount: PropTypes.number,
        date: PropTypes.string,
      }),
    ),
    companies: PropTypes.arrayOf(PropTypes.shape({})),
    types: PropTypes.arrayOf(
      PropTypes.shape({
        count: PropTypes.number,
        planName: PropTypes.string,
      }),
    ),
    renewals: PropTypes.arrayOf(PropTypes.shape({})),
    users: PropTypes.arrayOf(PropTypes.shape({})),
    overview: PropTypes.shape({
      assigned: PropTypes.arrayOf(
        PropTypes.shape({
          change: PropTypes.number,
          count: PropTypes.number,
          planName: PropTypes.string,
        }),
      ),
      licenses: PropTypes.arrayOf(
        PropTypes.shape({
          change: PropTypes.number,
          count: PropTypes.number,
          planName: PropTypes.string,
        }),
      ),
      users: PropTypes.shape({
        total: PropTypes.shape({
          change: PropTypes.number,
          count: PropTypes.number,
        }),
        licensed: PropTypes.shape({
          change: PropTypes.number,
          count: PropTypes.number,
        }),
      }),
    }),
  }),
  /**
   * If true, renders the all companies view, otherwise renders the specific company information
   */
  isAllCompaniesView: PropTypes.bool.isRequired,
  /**
   * Report end date
   */
  endDate: PropTypes.string,
  /**
   * Report start date
   */
  startDate: PropTypes.string,
  /**
   * Indicates if page is being shown in its print version.
   * Hides all interactive elements
   */
  print: PropTypes.bool,
  /**
   * Callback function to request list of user data by company
   * Pass customerId, returns array of user data used by license report user table
   */
  getUserDataByCompany: PropTypes.func,
  /**
   * Function provided to the Users table columns to listen when the sort direction changes
   */
  onSort: PropTypes.func,
  /**
   * Function provided to the Users table to listen when the filter settings change
   */
  afterFilter: PropTypes.func,
  /**
   * Function provided to the Users table to control the table filters remotely
   */
  getTableControls: PropTypes.func,

  afterLicenseFilter: PropTypes.func,
}

const defaultSorted = [{ dataField: 'name', order: 'asc' }]
const getSelectedLicenses = (licenseReportData) => {
  const savedLicenseFilter = localStorage.getItem(key)
  const selectedLicenses = savedLicenseFilter
    ? savedLicenseFilter.split(',')
    : licenseReportData.types?.map(({ planName }) => planName)

  return selectedLicenses || []
}
const getNegativeLicenseFilter = (selectedLicenses) => (license) =>
  !selectedLicenses.includes(license?.planName ?? license)
const getLicenseTrends = (selectedLicenses, licenseReportData = {}) => {
  const { types = [], trends = [], overview = {} } = licenseReportData

  if (selectedLicenses.length === types.length) {
    return trends
  }

  const { assigned = [] } = overview
  const nonSelectedLicenses = assigned.filter(getNegativeLicenseFilter(selectedLicenses))

  return trends.map(({ enabledCount, assignedCount, ...trend }, index) => ({
    ...trend,
    enabledCount: Math.max(
      0,
      nonSelectedLicenses.reduce((result, { total, change }) => result - total - (index ? 0 : change), enabledCount),
    ),
    assignedCount: Math.max(
      0,
      nonSelectedLicenses.reduce((result, { count, change }) => result - count - (index ? 0 : change), assignedCount),
    ),
  }))
}

function LicenseReport(props) {
  const {
    isLoading = false,
    isError = false,
    licenseReportData = {}, // all company high level data, or single tenant user data
    isAllCompaniesView,
    print = false,
    getUserDataByCompany = async () => {},
    onSort = null,
    afterFilter = null,
    getTableControls = null,
    afterLicenseFilter = noop,
  } = props ?? {}

  const intl = useIntl()

  const [allCompanyState, setAllCompanyState] = useState({}) // {<companyId>: {show: bool, loading:bool, data:arr, error:string}}
  const [selectedLicenses, setSelectedLicenses] = useState([])

  const hasSelectedLicense = (license) => selectedLicenses.includes(license?.planName ?? license)
  const doesntHaveSelectedLicense = getNegativeLicenseFilter(selectedLicenses)
  const types = licenseReportData.types?.filter(hasSelectedLicense) ?? []
  const trends = getLicenseTrends(selectedLicenses, licenseReportData)
  const renewals = isAllCompaniesView ? [] : (licenseReportData.renewals?.filter(hasSelectedLicense) ?? [])
  const filteredDataRef = useRef({})

  filteredDataRef.current = {
    renewals,
    trends,
    types,
    overview: {
      licenses: licenseReportData.overview?.licenses?.filter(hasSelectedLicense) ?? [],
      assigned: licenseReportData.overview?.assigned?.filter(hasSelectedLicense) ?? [],
    },
  }

  useEffect(() => {
    afterLicenseFilter(filteredDataRef.current)
  }, [afterLicenseFilter, selectedLicenses])

  useEffect(() => {
    setSelectedLicenses(getSelectedLicenses(licenseReportData))
  }, [licenseReportData])

  const getLineData = () => {
    const ownedLicenses = []
    const assignedLicenses = []
    const labels = []

    trends.forEach((trend) => {
      ownedLicenses.push(trend.enabledCount ?? 0)
      assignedLicenses.push(trend.assignedCount ?? 0)

      let dateLabel = moment(trend.date, dateFormat).format('MMM')
      if (labels.includes(dateLabel) || dateLabel === 'Invalid date') {
        dateLabel = ''
      }
      labels.push(dateLabel)
    })

    const dataset = {
      labels,
      datasets: [
        {
          label: intl.formatMessage({ id: 'SECURE.OWNED_LICENSES_TREND' }),
          data: ownedLicenses,
          fill: false,
          backgroundColor: '#343a40', // # fbe4e2 for red for inactive users
          borderColor: '#343a40', // #ee8278 for red for inactive users
          pointRadius: 2.5,
        },
        {
          label: intl.formatMessage({ id: 'SECURE.ASSIGNED_LICENSES_TREND' }),
          data: assignedLicenses,
          fill: false,
          backgroundColor: '#3399ff',
          borderColor: '#3399ff',
          pointRadius: 2.5,
        },
      ],
    }

    return dataset
  }

  const noDataLabel = () => (
    <div className={clsx('Uic-LicenseReport-no_data_base')}>
      <FormattedMessage id="UIC.GENERAL.NO_DATA" />
    </div>
  )

  const progressBar = (value, max, color = '#6c757d') => (
    <div className={clsx('Uic-LicenseReport-progress_bar_base')}>
      <Progress multi style={{ height: '8px' }}>
        <Progress bar style={{ backgroundColor: color, height: '8px' }} value={value} max={max} />
      </Progress>
    </div>
  )

  const tenantProgressBar = (value, max) => (
    <div
      style={{ margin: '6px 0 0 10px', padding: '0 14px 0 2px' }}
      className={clsx('Uic-LicenseReport-progress_bar_base')}
    >
      {progressBar(value, max, '#6c757d')}
    </div>
  )

  const licenseProgressBar = (value, max) => (
    <div
      style={{ margin: '6px 0 0 0px', padding: '1px 6px 0 0px' }}
      className={clsx('Uic-LicenseReport-progress_bar_base')}
    >
      {progressBar(value, max, '#3399ff')}
    </div>
  )

  const licenseTypesRow = (planName, count, max) => (
    <Row>
      <Col
        style={{ display: 'flex' }}
        sm="6"
        md="6"
        lg="6"
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
      >
        {planName}
      </Col>
      <Col
        style={{ display: 'flex' }}
        sm="6"
        md="6"
        lg="6"
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
      >
        {count}
        {tenantProgressBar(count, max || licenseReportData?.companies?.length || 1)}
      </Col>
    </Row>
  )

  const COMMITMENT_TO_MSG = {
    monthly: <FormattedMessage id="SECURE.LICENSE.MONTHLY" />,
    yearly: <FormattedMessage id="SECURE.LICENSE.YEARLY" />,
  }

  const licenseRenewalRow = (planName, subscription = {}) => {
    const { estimatedCommitment, nextLifecycleDateTime, subscriptionTotalLicenses } = subscription
    const commitment = COMMITMENT_TO_MSG[estimatedCommitment] || estimatedCommitment || '-'

    return (
      <Row>
        <Col
          style={{ display: 'flex' }}
          sm="5"
          md="5"
          lg="5"
          className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
        >
          {planName}
        </Col>
        <Col
          style={{ display: 'flex' }}
          sm="2"
          md="2"
          lg="2"
          className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
        >
          {commitment}
        </Col>
        <Col
          style={{ display: 'flex' }}
          sm="3"
          md="3"
          lg="3"
          className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
        >
          {nextLifecycleDateTime ? moment(nextLifecycleDateTime).format('MMM D YYYY') : '-'}
        </Col>
        <Col
          style={{ display: 'flex' }}
          sm="2"
          md="2"
          lg="2"
          className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
        >
          {subscriptionTotalLicenses}
        </Col>
      </Row>
    )
  }

  const enabledLicenseRow = (planName, count, change) => (
    <Row>
      <Col
        style={{ display: 'flex' }}
        sm="4"
        md="4"
        lg="4"
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
      >
        {planName}
      </Col>
      <Col
        style={{ display: 'flex' }}
        sm="4"
        md="4"
        lg="4"
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
      >
        {count}
      </Col>
      <Col
        style={{ display: 'flex' }}
        sm="4"
        md="4"
        lg="4"
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
      >
        {change > 0 ? `+${change}` : change}
      </Col>
    </Row>
  )

  const assignedLicenseRow = (planName, count, total, change) => (
    <Row>
      <Col
        style={{ display: 'flex' }}
        sm="4"
        md="4"
        lg="4"
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
      >
        {planName}
      </Col>
      <Col
        style={{ display: 'flex' }}
        sm="2"
        md="2"
        lg="2"
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
      >
        {count}/{total || <FormattedMessage id="UIC.GENERAL.UNKNOWN" />}
      </Col>
      <Col
        style={{ display: 'flex' }}
        sm="4"
        md="4"
        lg="4"
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
      >
        {licenseProgressBar(count, total || count)}
      </Col>
      <Col
        style={{ display: 'flex' }}
        sm="2"
        md="2"
        lg="2"
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
      >
        {change > 0 ? `+${change}` : change}
      </Col>
    </Row>
  )

  const overviewUsersRow = (count, change) => (
    <Row>
      <Col
        style={{ display: 'flex' }}
        sm="4"
        md="4"
        lg="4"
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
      >
        {count}
      </Col>
      <Col
        style={{ display: 'flex' }}
        sm="4"
        md="4"
        lg="4"
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_body')}
      >
        {change > 0 ? `+${change}` : change}
      </Col>
    </Row>
  )

  const renderLicenseTypesRows = () => types.map(({ planName, count }) => licenseTypesRow(planName, count))

  const renderLicenseRenewalRows = (renewals = [], options = {}) => {
    const { condensed } = options
    const rows = []

    renewals.filter(hasSelectedLicense).forEach((renewal) => {
      renewal?.subscriptions?.forEach((subscription, index) => {
        rows.push(licenseRenewalRow(index === 0 ? renewal.planName : '', subscription))
      })
      if (!condensed) {
        rows.push(<hr className="my-2" />)
      }
    })

    return rows
  }

  const renderOverviewEnabledLicensesRows = (data) => {
    const source = data || licenseReportData?.overview?.licenses || []
    const rows = []

    source.filter(hasSelectedLicense).forEach((e) => {
      rows.push(enabledLicenseRow(e.planName, e.count, e.change))
    })

    return rows
  }

  const renderOverviewAssignedLicensesRows = (data) => {
    const source = data || licenseReportData?.overview?.assigned || []
    const rows = []

    source.filter(hasSelectedLicense).forEach((e) => {
      rows.push(assignedLicenseRow(e.planName, e.count, e.total, e.change))
    })

    return rows
  }

  const renderOverviewTotalUsersRows = () => {
    const nonSelectedLicenses = licenseReportData.overview?.assigned?.filter(doesntHaveSelectedLicense)

    let count = licenseReportData?.overview?.users?.total?.count
    let change = licenseReportData?.overview?.users?.total?.change

    nonSelectedLicenses.forEach((license) => {
      count -= license.count
      change -= license.change
    })
    return overviewUsersRow(count, change)
  }

  const renderOverviewLicensedUsersRows = () => {
    const nonSelectedLicenses = licenseReportData.overview?.licenses?.filter(doesntHaveSelectedLicense)

    let count = licenseReportData?.overview?.users?.licensed?.count
    let change = licenseReportData?.overview?.users?.licensed?.change

    nonSelectedLicenses.forEach((license) => {
      count -= license.count
      change -= license.change
    })
    return overviewUsersRow(count, change)
  }

  // eslint-disable-next-line consistent-return
  const formatArrayColumn = (row, rowType) => {
    if (row && row[rowType]) {
      const returnArray = []
      let collapsed = ''

      for (let i = 0; i < row[rowType].length; i += 1) {
        if (i < 1 || print) {
          returnArray.push(
            <div key={i} className={clsx('Uic-LicenseReport-chip', 'Uic-LicenseReport-chip_multiline')}>
              {row[rowType][i]}
            </div>,
          )
        } else {
          collapsed += !collapsed.length ? row[rowType][i] : `, ${row[rowType][i]}`
        }
      }

      const renderTooltip = (props) => (
        <Tooltip id="button-tooltip" {...props}>
          {collapsed}
        </Tooltip>
      )

      if (row[rowType].length > 1 && !print) {
        returnArray.push(
          <div key={1}>
            <OverlayTrigger
              placement="top"
              delay={{ show: 250, hide: 400 }}
              overlay={renderTooltip}
              trigger={['hover', 'focus', 'click']}
            >
              <div className={clsx('Uic-LicenseReport-chip', 'Uic-LicenseReport-chip_multiline')}>
                {' '}
                +{row[rowType].length - 1}{' '}
              </div>
            </OverlayTrigger>
          </div>,
        )
      }
      return returnArray
    }
  }

  const formatEmployeeColumn = (cell, row) => (
    <>
      <div className={clsx('Uic-LicenseReport-colour_dark_text', 'Uic-LicenseReport-employee_column')}>{cell}</div>
      <div className={clsx('Uic-LicenseReport-colour_light_text', 'Uic-LicenseReport-employee_column')}>
        {row.email}
      </div>
    </>
  )

  const formatRoleColumn = (cell, row) => {
    if (!Array.isArray(row.roles)) {
      row.roles = [row.roles]
    }
    return formatArrayColumn(row, 'roles')
  }

  const formatLicenseTypeColumn = (cell, row) => {
    if (!Array.isArray(row.licenses)) {
      row.licenses = [row.licenses]
    }
    return formatArrayColumn(row, 'licenses')
  }

  const formatChangeColumn = (cell, row) => {
    if (!Array.isArray(row.change)) {
      row.change = [row.change]
    }
    return formatArrayColumn(row, 'change')
  }

  // on the fly build the user columns from the users passed in
  const buildUserColumns = (users) => [
    {
      dataField: 'name',
      text: intl.formatMessage({
        id: 'SECURE.EMPLOYEE',
      }),
      headerStyle: employeeColumnHeaderStyle,
      formatter: formatEmployeeColumn,
      sort: !print,
      onSort,
      filter: !print
        ? {
            type: Table.FILTER_TYPES.TEXT,
          }
        : null,
    },
    {
      dataField: 'roles',
      text: intl.formatMessage({
        id: 'SECURE.ROLE',
      }),
      formatter: formatRoleColumn,
      filter: !print
        ? {
            type: Table.FILTER_TYPES.MULTISELECT,
            options: getTableOptions(users, 'roles'),
          }
        : null,
    },
    {
      dataField: 'licenses',
      text: intl.formatMessage({
        id: 'SECURE.LICENSE_TYPE',
      }),
      formatter: formatLicenseTypeColumn,
      filter: !print
        ? {
            type: Table.FILTER_TYPES.MULTISELECT,
            options: getTableOptions(users, 'licenses'),
          }
        : null,
    },
    {
      dataField: 'change',
      text: intl.formatMessage({
        id: 'SECURE.CHANGE_PREV_MONTH',
      }),
      formatter: formatChangeColumn,
      filter: !print
        ? {
            type: Table.FILTER_TYPES.MULTISELECT,
            options: getTableOptions(users, 'change'),
          }
        : null,
    },
    {
      dataField: 'status',
      text: intl.formatMessage({
        id: 'SECURE.LICENSE_STATUS',
      }),
      filter: !print
        ? {
            type: Table.FILTER_TYPES.MULTISELECT,
            options: getTableOptions(users, 'status'),
          }
        : null,
    },
  ]

  const formatTotalSizeMessage = (totalSize) => (
    <h1 className="Uic-LicenseReport-total_size_message">
      <FormattedMessage id="UIC.GENERAL.USERS" values={{ users: totalSize }} />
    </h1>
  )

  const allCompaniesCompanyCard = (company) => {
    const companyState = allCompanyState[company?.id] || {
      data: [],
      show: false,
      loading: false,
      error: null,
    }
    return (
      <Row>
        {/* Per Company Table Component */}
        <Col sm="12" md="12" lg="12">
          <GenericPortlet
            isLoading={isLoading}
            isError={isError}
            hasHeader
            portletTitle={`${company?.name}`}
            hasFooter
            hasFooterTable
            showVerticalSeparators
            portletBodyRowClass="align-items-center"
            verticalSeparatorHeight="171px"
            showHorizontalHeaderBreak
            showFooterButton
            footerBackgroundColor={companyState.show ? '#F8F9FA' : '#FFF'}
            showTableBelowFooter={companyState.show}
            onFooterButtonClick={async () => {
              const newState = { ...companyState }

              // Show the view
              newState.show = !newState.show

              // If showing, then load the user data, otherwise hide
              if (newState.show) {
                // Loading (show loading spinner for section)
                newState.loading = true
                setAllCompanyState({
                  ...allCompanyState,
                  [company?.id]: newState,
                })

                // Get the user data for the company and show
                try {
                  const data = (await getUserDataByCompany(company?.id)) ?? []
                  newState.data = data
                } catch (e) {
                  newState.error = e
                }
                newState.loading = false
              }
              setAllCompanyState({
                ...allCompanyState,
                [company?.id]: newState,
              })
            }}
            footerButtonTextId={companyState.show ? 'SECURE.LICENSE_HIDE_USERS' : 'SECURE.LICENSE_VIEW_USERS'}
            innerPortletWrapperStyle={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
            rowComponents={1}
          >
            <Row>
              <Col sm="4" md="4" lg="4">
                <span className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-overview_header')}>
                  <FormattedMessage id="SECURE.ASSIGNED_LICENSES" />
                </span>
                <Row>
                  <Col
                    style={{ display: 'flex' }}
                    sm="4"
                    md="4"
                    lg="4"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.LICENSE_PLAN" />
                  </Col>
                  <Col
                    style={{ display: 'flex' }}
                    sm="4"
                    md="4"
                    lg="4"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.NUMBER_OF_LICENSES_ENABLED" />
                  </Col>
                  <Col
                    style={{ display: 'flex' }}
                    sm="4"
                    md="4"
                    lg="4"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.CHANGE" />
                  </Col>
                </Row>

                {company?.licenses != null ? renderOverviewEnabledLicensesRows(company?.licenses) : noDataLabel()}
              </Col>

              <Col sm="4" md="4" lg="4">
                <span className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-overview_header')}>
                  <FormattedMessage id="SECURE.OWNED_LICENSES" />
                </span>
                <Row>
                  <Col
                    style={{ display: 'flex' }}
                    sm="4"
                    md="4"
                    lg="4"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.LICENSE_PLAN" />
                  </Col>
                  <Col
                    style={{ display: 'flex' }}
                    sm="6"
                    md="6"
                    lg="6"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.NUMBER_OF_LICENSES" />
                  </Col>
                  <Col
                    style={{ display: 'flex' }}
                    sm="4"
                    md="4"
                    lg="2"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.CHANGE" />
                  </Col>
                </Row>

                {company?.assigned != null ? renderOverviewAssignedLicensesRows(company?.assigned) : noDataLabel()}
              </Col>

              <Col sm="4" md="4" lg="4">
                <span className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-overview_header')}>
                  <FormattedMessage id="SECURE.LICENSE.RENEWAL" />
                </span>
                <Row>
                  <Col
                    style={{ display: 'flex' }}
                    sm="5"
                    md="5"
                    lg="5"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.LICENSE_PLAN" />
                  </Col>
                  <Col
                    style={{ display: 'flex' }}
                    sm="2"
                    md="2"
                    lg="2"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.LICENSE.COMMITMENT" />
                  </Col>
                  <Col
                    style={{ display: 'flex' }}
                    sm="3"
                    md="3"
                    lg="3"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.LICENSE.DATE" />
                  </Col>
                  <Col
                    style={{ display: 'flex' }}
                    sm="2"
                    md="2"
                    lg="2"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.LICENSE.OWNED" />
                  </Col>
                </Row>

                {company?.renewals
                  ? renderLicenseRenewalRows(company?.renewals, {
                      condensed: true,
                    })
                  : noDataLabel()}
              </Col>
            </Row>
            <Row>
              <Col sm="12" md="12" lg="12">
                {companyState.loading ? (
                  <Spinner className="Uic-LicenseReport-center" />
                ) : (
                  <Table
                    keyField="id"
                    data={companyState?.data?.users?.filter((user) => user.licenses.some(hasSelectedLicense)) || []}
                    columns={buildUserColumns(companyState?.data?.users || [])}
                    formatTotalSizeMessage={formatTotalSizeMessage}
                    loading={isLoading}
                    error={companyState.error && intl.formatMessage({ id: 'UIC.TABLE.ERROR' })}
                    hidePagination={print}
                    hideHeader
                    borderless
                  />
                )}
              </Col>
            </Row>
          </GenericPortlet>
        </Col>
      </Row>
    )
  }

  const renderCompanyForAllCompaniesView = () => {
    const rows = []

    licenseReportData?.companies?.forEach((e) => {
      rows.push(allCompaniesCompanyCard(e))
    })

    return rows
  }

  const displayTableView = ({ licenseReportData, isAllCompaniesView }) => {
    if (isAllCompaniesView) {
      if (licenseReportData?.companies?.length > 0) {
        return renderCompanyForAllCompaniesView()
      }
      return noDataLabel()
    }

    const filteredUsers = licenseReportData?.users?.filter((user) => user.licenses.some(hasSelectedLicense))

    // Single Company view - show single User table
    return (
      <Table
        defaultSorted={defaultSorted}
        keyField="id"
        data={filteredUsers}
        columns={buildUserColumns(filteredUsers)}
        formatTotalSizeMessage={formatTotalSizeMessage}
        loading={isLoading}
        error={isError && intl.formatMessage({ id: 'UIC.TABLE.ERROR' })}
        hidePagination={print}
        afterFilter={afterFilter}
        getTableControls={getTableControls}
      />
    )
  }

  return (
    <>
      {!print && (
        <Row>
          <Col>
            <div className={clsx('Uic-LicenseReport-overview_header', 'p-0')}>
              <FormattedMessage id="LICENSE_REPORT.FILTER_LICENSES" />

              <MultiSelectSearch
                id="multiSelect"
                placeholder={intl.formatMessage({
                  id: 'LICENSE_REPORT.FILTER_LICENSES_PLACEHOLDER',
                })}
                options={getTableOptions(licenseReportData.types, 'planName')}
                remoteValue={selectedLicenses}
                onChange={(item) => {
                  localStorage.setItem(key, item)
                  setSelectedLicenses(item)
                }}
              />
            </div>
          </Col>
        </Row>
      )}

      <Row className="align-items-stretch">
        {/* Total - License Types */}
        {isAllCompaniesView && (
          <Col style={{ display: 'flex' }} sm="4" md="4" lg="4">
            <GenericPortlet
              isLoading={isLoading}
              isError={isError}
              hasHeader
              showHorizontalHeaderBreak
              portletTitle={intl.formatMessage({
                id: 'SECURE.LICENSE_TYPES',
              })}
              showInfoTooltip={!print}
              infoTooltipPlacement="right"
              portletBodyRowClass="align-items-center"
              innerPortletWrapperStyle={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              portletBodyStyle={{ height: '100%' }}
              infoTooltipTextId="SECURE.LICENSE_TYPES_INFO_DEFAULT"
              fluidHeight
              portletBodyClassName={licenseReportData?.types?.length ? 'pt-0' : 'p-0'}
            >
              <div className={clsx('Uic-LicenseReport-license_types_header')}>
                <Row>
                  <Col
                    style={{ display: 'flex' }}
                    sm="6"
                    md="6"
                    lg="6"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.LICENSE_TYPE" />
                  </Col>
                  <Col
                    style={{ display: 'flex' }}
                    sm="6"
                    md="6"
                    lg="6"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.NUMBER_OF_TENANTS" />
                  </Col>
                </Row>

                {licenseReportData?.types != null ? renderLicenseTypesRows() : noDataLabel()}
              </div>
            </GenericPortlet>
          </Col>
        )}

        {!isAllCompaniesView && (
          <Col style={{ display: 'flex' }} sm="6" md="6" lg="6">
            <GenericPortlet
              isLoading={isLoading}
              isError={isError}
              hasHeader
              showHorizontalHeaderBreak
              portletTitle={intl.formatMessage({
                id: 'SECURE.LICENSE_RENEWALS',
              })}
              showInfoTooltip={!print}
              infoTooltipPlacement="right"
              portletBodyRowClass="align-items-center"
              innerPortletWrapperStyle={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              portletBodyStyle={{ height: '100%' }}
              infoTooltipTextId="SECURE.LICENSE_TYPES_INFO_DEFAULT"
              fluidHeight
              portletBodyClassName={licenseReportData?.types?.length ? 'pt-0' : 'p-0'}
            >
              <div className={clsx('Uic-LicenseReport-license_types_header')}>
                <Row>
                  <Col
                    style={{ display: 'flex' }}
                    sm="5"
                    md="5"
                    lg="5"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.LICENSE_TYPE" />
                  </Col>
                  <Col
                    style={{ display: 'flex' }}
                    sm="2"
                    md="2"
                    lg="2"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.ESTIMATED_COMMITMENT" />
                  </Col>
                  <Col
                    style={{ display: 'flex' }}
                    sm="3"
                    md="3"
                    lg="3"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.RENEWAL_DATE" />
                  </Col>
                  <Col
                    style={{ display: 'flex' }}
                    sm="2"
                    md="2"
                    lg="2"
                    className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                  >
                    <FormattedMessage id="SECURE.OWNED_LICENSES" />
                  </Col>
                </Row>
                <hr className="mt-0 mb-2" />
                {licenseReportData?.renewals ? renderLicenseRenewalRows(licenseReportData?.renewals) : noDataLabel()}
              </div>
            </GenericPortlet>
          </Col>
        )}

        {/* Total - License Trends Widget */}
        <Col
          style={{ display: 'flex' }}
          sm={isAllCompaniesView ? '8' : '6'}
          md={isAllCompaniesView ? '8' : '6'}
          lg={isAllCompaniesView ? '8' : '6'}
        >
          <GenericPortlet
            isLoading={isLoading}
            isError={isError}
            hasHeader
            portletTitle={intl.formatMessage({
              id: 'SECURE.LICENSE_TRENDS',
            })}
            showHorizontalHeaderBreak
            portletBodyRowClass="align-items-center"
            portletBodyClassName={licenseReportData?.trends?.length ? 'pb-0' : 'pt-0 pb-0'}
            portletBodyStyle={{ flexGrow: 1 }}
            innerPortletWrapperStyle={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }} // Must wrap the generic portlet in a div with flex to allow inner components to flex grow to fill height
          >
            <div
              className={clsx(
                'flex-fill',
                'Uic-LicenseReport-font_bolder',
                'Uic-LicenseReport-line_cont',
                print && 'Uic-LicenseReport-lineChartPrint',
              )}
            >
              <LineChart heading="" data={getLineData()} />
            </div>
          </GenericPortlet>
        </Col>
      </Row>

      {/* TODO: All Companies Overview */}
      <Row className="align-items-stretch">
        <Col style={{ display: 'flex' }} sm="12" md="12" lg="12">
          <GenericPortlet
            isLoading={isLoading}
            isError={isError}
            hasHeader
            portletTitle={
              <FormattedMessage id={isAllCompaniesView ? 'SECURE.ALL_COMPANIES_OVERVIEW' : 'SECURE.LICENSE_OVERVIEW'} />
            }
            showHorizontalHeaderBreak
            showVerticalSeparators
            portletBodyRowClass="align-items-center"
            innerPortletWrapperStyle={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <>
              <Row>
                <Col sm="6" md="6" lg="6">
                  <span className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-overview_header')}>
                    <FormattedMessage id="SECURE.ASSIGNED_LICENSES" />
                  </span>
                  <Row>
                    <Col
                      style={{ display: 'flex' }}
                      sm="4"
                      md="4"
                      lg="4"
                      className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                    >
                      <FormattedMessage id="SECURE.LICENSE_PLAN" />
                    </Col>
                    <Col
                      style={{ display: 'flex' }}
                      sm="4"
                      md="4"
                      lg="4"
                      className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                    >
                      <FormattedMessage id="SECURE.NUMBER_OF_LICENSES_ENABLED" />
                    </Col>
                    <Col
                      style={{ display: 'flex' }}
                      sm="4"
                      md="4"
                      lg="4"
                      className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                    >
                      <FormattedMessage id="SECURE.CHANGE" />
                    </Col>
                  </Row>

                  {licenseReportData?.overview?.licenses != null ? renderOverviewEnabledLicensesRows() : noDataLabel()}
                </Col>

                <Col sm="6" md="6" lg="6">
                  <span className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-overview_header')}>
                    <FormattedMessage id="SECURE.OWNED_LICENSES" />
                  </span>
                  <Row>
                    <Col
                      style={{ display: 'flex' }}
                      sm="4"
                      md="4"
                      lg="4"
                      className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                    >
                      <FormattedMessage id="SECURE.LICENSE_PLAN" />
                    </Col>
                    <Col
                      style={{ display: 'flex' }}
                      sm="6"
                      md="6"
                      lg="6"
                      className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                    >
                      <FormattedMessage id="SECURE.NUMBER_OF_LICENSES" />
                    </Col>
                    <Col
                      style={{ display: 'flex' }}
                      sm="2"
                      md="2"
                      lg="2"
                      className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                    >
                      <FormattedMessage id="SECURE.CHANGE" />
                    </Col>
                  </Row>

                  {licenseReportData?.overview?.assigned != null ? renderOverviewAssignedLicensesRows() : noDataLabel()}
                </Col>
              </Row>

              {isAllCompaniesView && (
                <Row style={{ marginTop: '4rem' }}>
                  <Col sm="4" md="4" lg="4">
                    <span className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-overview_header')}>
                      <FormattedMessage id="SECURE.LICENSE_USERS" />
                    </span>

                    <Row>
                      <Col
                        style={{ display: 'flex' }}
                        sm="4"
                        md="4"
                        lg="4"
                        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                      >
                        <FormattedMessage id="SECURE.TOTAL_USERS" />
                      </Col>
                      <Col
                        style={{ display: 'flex' }}
                        sm="4"
                        md="4"
                        lg="4"
                        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                      >
                        <FormattedMessage id="SECURE.CHANGE_PREV_MONTH" />
                      </Col>
                    </Row>

                    {licenseReportData?.overview?.users?.total != null ? renderOverviewTotalUsersRows() : noDataLabel()}
                  </Col>

                  <Col sm="4" md="4" lg="4">
                    <span className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-overview_header')} />

                    <Row>
                      <Col
                        style={{ display: 'flex' }}
                        sm="4"
                        md="4"
                        lg="4"
                        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                      >
                        <FormattedMessage id="SECURE.TOTAL_LICENSED_USERS" />
                      </Col>
                      <Col
                        style={{ display: 'flex' }}
                        sm="4"
                        md="4"
                        lg="4"
                        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-license_types_header')}
                      >
                        <FormattedMessage id="SECURE.CHANGE_PREV_MONTH" />
                      </Col>
                    </Row>

                    {licenseReportData?.overview?.users?.total != null
                      ? renderOverviewLicensedUsersRows()
                      : noDataLabel()}
                  </Col>
                </Row>
              )}
            </>
          </GenericPortlet>
        </Col>
      </Row>

      {/* TODO: # of companies / users */}
      <p
        className={clsx('Uic-LicenseReport-font_bolder', 'Uic-LicenseReport-number_licenses')}
        style={{ fontSize: '14px', marginLeft: '14px' }}
      >
        {isAllCompaniesView && (
          <>
            {`${licenseReportData?.companies?.length || ''} `}
            {licenseReportData?.companies?.length > 1 ? (
              <FormattedMessage id="SECURE.LICENSE_COMPANIES" />
            ) : (
              <FormattedMessage id="SECURE.LICENSE_COMPANY" />
            )}
          </>
        )}
      </p>

      {
        // All companies view - show multiple company panels
        displayTableView({ licenseReportData, isAllCompaniesView })
      }
    </>
  )
}

LicenseReport.propTypes = propTypes

LicenseReport.localStorageKey = key

export default LicenseReport
